import { WorkflowRole } from '@exam-management/common/types';
import {
  OrderType,
  QuestionGroupType,
  useGetDownloadSignedUrlForQuestionsByQSetIdQuery,
  useGetQSetTaskReviewSummaryQuery,
  useGetReviewQSetSubtaskDetailsByIdQuery,
  useSearchTaskReviewedQSetQuestionsQuery,
} from '@exam-management/graphql';
import { mapSignedUrlToImages } from '@exam-management/qset-review-subtask-details';
import { getWorkflowRoleForSubtask } from '@exam-management/task-management';
import { useAuth } from '@shared/auth';
import { ErrorBox, Loading } from '@shared/components';
import { createContext, useMemo, type PropsWithChildren } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useSubTaskDetails } from '../../hooks';
import {
  type QuestionSubTaskVerdict,
  type QSetReviewMainTaskQSetContextData,
  type QuestionSubTaskComment,
} from '../../types';

export const QSetReviewMainTaskQSetContext =
  createContext<QSetReviewMainTaskQSetContextData | null>(null);

interface QSetReviewMainTaskQSetContextProps extends PropsWithChildren {
  mainTaskId: string;
  qSetId: string;
}

export function QSetReviewMainTaskQSetProvider(
  props: Readonly<QSetReviewMainTaskQSetContextProps>,
) {
  const { mainTaskId, qSetId, children } = props;
  const { userProfile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const order = Number(searchParams.get('order') ?? 1);
  const {
    data: searchedQSetQuestions,
    loading: isSearchingQSetQuestions,
    error: isErrorSearchingQSetQuestions,
  } = useSearchTaskReviewedQSetQuestionsQuery({
    variables: {
      input: {
        taskId: mainTaskId,
        qSetId: qSetId,
        paging: {
          pageSize: 200, // NOTED: Get all questions
          currentPage: 0,
        },
        order: {
          field: 'order',
          type: OrderType.Asc,
        },
      },
    },
  });

  const {
    data: getDownloadSignedUrlForQuestionsByQSetId,
    loading: isGettingDownloadSignedUrlForQuestionsByQSetId,
    error: isErrorGetDownloadSignedUrlForQuestionsByQSetId,
  } = useGetDownloadSignedUrlForQuestionsByQSetIdQuery({
    variables: {
      input: {
        qSetId: qSetId,
        taskId: mainTaskId,
      },
    },
    skip: !qSetId || !mainTaskId,
  });

  const {
    data,
    error: isErrorQSetReviewMainTaskDetail,
    loading: isLoadingQSetReviewMainTaskDetail,
  } = useGetReviewQSetSubtaskDetailsByIdQuery({
    variables: {
      getTaskByIdInput: {
        taskId: mainTaskId,
      },
    },
  });

  const signedUrls: Record<
    string,
    Record<string, string>
  > = getDownloadSignedUrlForQuestionsByQSetId?.excGetDownloadSignedUrlForQuestionsByQSetId
    .signedUrls;

  const {
    data: getQSetTaskReviewSummaryData,
    loading: isGettingQSetTaskReviewSummary,
    error: isErrorGettingQSetTaskReviewSummary,
  } = useGetQSetTaskReviewSummaryQuery({
    variables: {
      getQSetTaskReviewSummaryInput: {
        qSetId: qSetId,
        mainTaskId: mainTaskId,
      },
    },
    skip: !qSetId || !mainTaskId,
  });

  // get subTaskIds
  const subTaskIds = getQSetTaskReviewSummaryData?.wflGetQSetTaskReviewSummary.subTaskIds ?? [];

  const { subTaskDetails, isLoading: isLoadingSubTaskDetails } = useSubTaskDetails(subTaskIds);

  const [isOwner, isAssigner, isAssignee] = useMemo(() => {
    if (!data) return [false, false, false];
    const workflowRoles = getWorkflowRoleForSubtask({
      userId: userProfile?.id ?? '',
      assigneeId: '-',
      assignerId: data?.wflGetTaskById.task.assignerId ?? '',
      parentOwnerId: data?.wflGetTaskById.task.parentTask?.ownerId ?? '',
    });

    const [isOwner, isAssigner, isAssignee] = workflowRoles.reduce(
      (acc, role) => [
        acc[0] || role === WorkflowRole.TASK_OWNER,
        acc[1] || role === WorkflowRole.SUBTASK_ASSIGNER,
        acc[2] || role === WorkflowRole.SUBTASK_ASSIGNEE,
      ],
      [false, false, false],
    );
    return [isOwner, isAssigner, isAssignee];
  }, [data, userProfile]);

  // sort getQSetTaskReviewSummaryData by order of questions in searchedQSetQuestions
  const sortedQSetTaskReviewSummaryResultData =
    getQSetTaskReviewSummaryData?.wflGetQSetTaskReviewSummary.results.slice().sort((a, b) => {
      return a.question.order - b.question.order;
    });

  const qSetQuestions = useMemo(() => {
    if (!searchedQSetQuestions?.excSearchTaskQSetQuestions?.qSetQuestions || !signedUrls) {
      return [];
    }

    return searchedQSetQuestions?.excSearchTaskQSetQuestions?.qSetQuestions
      .filter(
        // NOTE: We did some TypeScript magic here to make sure that the question is not null.
        // Since TypeScript doesn't know that the question is not null, we have to do this.
        (
          qSetQuestion,
        ): qSetQuestion is typeof qSetQuestion & {
          question: NonNullable<typeof qSetQuestion.question>;
        } => !!qSetQuestion.question,
      )
      .sort((a, b) => a.order - b.order)
      .map((qSetQuestion) => {
        return {
          ...qSetQuestion.question,
          details: mapSignedUrlToImages(
            qSetQuestion.question.details,
            signedUrls[qSetQuestion.questionId],
          ),
          solution: {
            type: qSetQuestion.question.solution?.type ?? null,
            value: mapSignedUrlToImages(
              qSetQuestion.question.solution?.value ?? '',
              signedUrls[qSetQuestion.questionId],
            ),
          },
          metadata: {
            ...qSetQuestion.question.metadata,
            choices: qSetQuestion.question.metadata.choices.map((choice) => ({
              ...choice,
              label: mapSignedUrlToImages(choice.label, signedUrls[qSetQuestion.questionId]),
            })),
          },
          questionGroup: {
            ...qSetQuestion.question.questionGroup,
            type: qSetQuestion.question.questionGroup?.type ?? QuestionGroupType.ItemSet,
            refCode: qSetQuestion.question.questionGroup?.refCode ?? '',
            size: qSetQuestion.question.questionGroup?.size ?? null,
            metadata: {
              ...qSetQuestion.question.questionGroup?.metadata,
              paragraph: mapSignedUrlToImages(
                qSetQuestion.question.questionGroup?.metadata?.paragraph ?? '',
                signedUrls[qSetQuestion.questionGroupId],
              ),
            },
          },
        };
      });
  }, [searchedQSetQuestions, signedUrls]);

  const currentQuestion = useMemo(() => {
    const qNo = Number(order);
    if (isNaN(qNo) || !qSetQuestions[qNo - 1]) return;
    return qSetQuestions[qNo - 1];
  }, [qSetQuestions, order]);

  const mappedVerdictBySubTaskId: Map<string, QuestionSubTaskVerdict> = useMemo(() => {
    const currentQuestionSubTaskVerdict =
      sortedQSetTaskReviewSummaryResultData?.[order - 1]?.verdicts ?? [];
    return new Map(currentQuestionSubTaskVerdict.map((verdict) => [verdict.taskId, verdict]));
  }, [sortedQSetTaskReviewSummaryResultData, order]);

  // map subTaskId to array of comments, sorted by createdAt in ascending order
  const mappedCommentBySubTaskId: Map<string, QuestionSubTaskComment[]> = useMemo(() => {
    const currentQuestionSubTaskComments =
      sortedQSetTaskReviewSummaryResultData?.[order - 1]?.comments ?? [];
    const sortedComments = currentQuestionSubTaskComments.slice().sort((a, b) => {
      // sort by ascending order of createdAt
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });
    const commentMap = new Map<string, QuestionSubTaskComment[]>();
    for (const comment of sortedComments) {
      if (!commentMap.has(comment.taskId)) {
        commentMap.set(comment.taskId, []);
      }
      commentMap.get(comment.taskId)?.push(comment);
    }
    return commentMap;
  }, [sortedQSetTaskReviewSummaryResultData, order]);

  const currentVerdict = useMemo(() => {
    if (!sortedQSetTaskReviewSummaryResultData) return [];
    return sortedQSetTaskReviewSummaryResultData[order - 1]?.verdicts ?? [];
  }, [sortedQSetTaskReviewSummaryResultData, order]);

  const getCurrentQuestionSubTaskVerdict = (subTaskId: string): QuestionSubTaskVerdict | null => {
    return mappedVerdictBySubTaskId.get(subTaskId) ?? null;
  };

  const getCurrentQuestionSubTaskComments = (subTaskId: string): QuestionSubTaskComment[] => {
    return mappedCommentBySubTaskId.get(subTaskId) ?? [];
  };

  const handleChangeCurrentQuestionNumber = (questionNumber: number) => {
    const currentQuestionNumber =
      questionNumber > qSetQuestions.length ? qSetQuestions.length : questionNumber;
    setSearchParams(
      { order: currentQuestionNumber === 0 ? '1' : currentQuestionNumber.toString() },
      { replace: true },
    );
    //document.getElementById('review-qset-exam-details')?.scrollIntoView();
  };

  const contextValue: QSetReviewMainTaskQSetContextData = {
    subTasks: subTaskDetails,
    roles: {
      isOwner,
      isAssigner,
      isAssignee,
    },
    qSetQuestion: {
      currentQuestionNumber: order,
      currentQuestion,
      questions: qSetQuestions,
    },
    currentVerdict,
    getCurrentQuestionSubTaskVerdict,
    setCurrentQuestionNumber: handleChangeCurrentQuestionNumber,
    getCurrentQuestionSubTaskComments,
  };

  if (
    isErrorSearchingQSetQuestions ||
    isErrorGettingQSetTaskReviewSummary ||
    isErrorGetDownloadSignedUrlForQuestionsByQSetId ||
    isErrorQSetReviewMainTaskDetail
  ) {
    return <ErrorBox />;
  }

  if (
    isSearchingQSetQuestions ||
    isGettingQSetTaskReviewSummary ||
    isGettingDownloadSignedUrlForQuestionsByQSetId ||
    isLoadingQSetReviewMainTaskDetail ||
    isLoadingSubTaskDetails
  ) {
    return <Loading sx={{ height: '100dvh' }} />;
  }

  return (
    <QSetReviewMainTaskQSetContext.Provider value={contextValue}>
      {children}
    </QSetReviewMainTaskQSetContext.Provider>
  );
}
